// KPI Builder — keeps the existing visualization form in step with the builder's answers.
// The builder never saves anything itself: it writes the generated query/config into the same
// form fields the raw JSON editors use, so the existing validation and Save flow stay in charge.
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  builderAnswersState, builderModeState, builderOutputState, currentVisualizationState, visualizationsState,
} from 'states';
import { splitConfig } from 'helpers/visualizationHelpers';
import { autoTitle, codeFromTitle, compile, isDataComplete } from 'helpers/kpiBuilder/engine';
import { restoreAnswers } from 'helpers/kpiBuilder/restoreAnswers';
import { BuilderField, FormBridge } from 'helpers/kpiBuilder/formBridge';
import { useBuilderIssues } from './useBuilderIssues';
import { useModeSwitches } from './useModeSwitches';
import { useBuilderEdits } from './useBuilderEdits';
import { BuilderAnswers, BuilderOutput, EMPTY_ANSWERS } from 'helpers/kpiBuilder/types';

/** Write the generated query and config into the form, or blank them while the answers are incomplete. */
function writeGenerated(form: FormBridge, answers: BuilderAnswers, title: string, code?: string): BuilderOutput | null {
  if (!isDataComplete(answers)) {
    ['queries', 'config', 'chartType'].forEach((f) => form.set(f as BuilderField, ''));
    form.set('kpiFilters', '[]');
    return null;
  }
  const out = compile(answers, title, code);
  const split = splitConfig(JSON.stringify(out.config));
  form.set('queries', out.queryString, true);
  form.set('config', split.restConfigStr, true);
  form.set('kpiFilters', split.kpiFiltersStr, true);
  form.set('chartType', out.chartType, true);
  return out;
}

export function useBuilderSync(form: FormBridge, title: string) {
  const currentVisualization = useRecoilValue(currentVisualizationState);
  const answers = useRecoilValue(builderAnswersState);
  const allVisualizations = useRecoilValue(visualizationsState);
  const builderMode = useRecoilValue(builderModeState);
  const setAnswers = useSetRecoilState(builderAnswersState);
  const setMode = useSetRecoilState(builderModeState);
  const setOutput = useSetRecoilState(builderOutputState);
  const [switchedToJson, setSwitchedToJson] = useState(false);
  const lastAutoTitle = useRef('');
  // Answers exactly as restored from a saved KPI. Until the admin changes one, the saved query is left alone.
  const savedAnswers = useRef('');
  const isExisting = !!currentVisualization?.id;
  const existingCode = currentVisualization?.visualizationCode;

  // Open a visualization: builder-made ones reopen in the builder, hand-written ones in the JSON editors.
  useEffect(() => {
    const restored = restoreAnswers(currentVisualization, allVisualizations);
    const next = restored ? { ...EMPTY_ANSWERS, ...restored } : EMPTY_ANSWERS;
    savedAnswers.current = isExisting ? JSON.stringify(next) : '';
    setAnswers(next);
    setMode(!isExisting || !!restored);
    setSwitchedToJson(false);
    // The placeholder title a new visualization starts with counts as "not typed by the admin".
    lastAutoTitle.current = isExisting ? '' : currentVisualization?.title ?? '';
    // Only opening a different visualization resets the builder; the other values are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVisualization]);

  // New visualizations: suggest a title from the answers until the admin types their own.
  useEffect(() => {
    if (!builderMode || isExisting) return;
    const auto = autoTitle(answers);
    if (!auto) return;
    const current = form.get('title');
    if (!current || current === lastAutoTitle.current) form.set('title', auto, true);
    lastAutoTitle.current = auto;
    // form is a new object every render; only a change of answers should suggest a new title.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // New visualizations: the code follows the title.
  useEffect(() => {
    if (builderMode && !isExisting && title) form.set('visualizationCode', codeFromTitle(title), true);
    // form is a new object every render; only the title and mode should regenerate the code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, builderMode]);

  // Generate the query and config into the form fields.
  useEffect(() => {
    if (!builderMode) return;
    // An existing KPI keeps its stored query until an answer is actually changed.
    const current = JSON.stringify(answers);
    if (isExisting && (current === savedAnswers.current || current === JSON.stringify(EMPTY_ANSWERS))) {
      // Show what the answers would generate (for the Additional Config tab) without touching the form.
      setOutput(isDataComplete(answers) ? compile(answers, title, existingCode) : null);
      return;
    }
    // A copy whose original is gone cannot be rebuilt; keep what is stored rather than blanking it.
    if (isExisting && answers.recipe === 'template' && !isDataComplete(answers)) return;
    setOutput(writeGenerated(form, answers, title, isExisting ? existingCode : undefined));
    // form is a new object every render; only answers, title and mode should regenerate the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, title, builderMode]);

  useBuilderIssues(answers, title, builderMode);
  useBuilderEdits(form, builderMode);

  const { switchToJson, switchToBuilder } = useModeSwitches(form, answers, currentVisualization, setSwitchedToJson);

  return {
    builderMode,
    switchToJson,
    switchToBuilder,
    canSwitchToBuilder: !builderMode,
    keepGeneratedFields: isExisting || (switchedToJson && isDataComplete(answers)),
  };
}
